/**
 * Marketing (Fase G) — one home for everything marketing/analytics per org:
 *   • Píxeles   → GA4 / Meta / GTM tracking IDs (TrackingConfigEditor, G.3)
 *   • UTM       → campaign URL builder + saved history (G.2)
 *   • Campañas  → visits report (+ sales when the org has ecommerce) (G.2)
 *
 * Visibility of the whole section is gated by marketing:read in the sidebar.
 * Inside, the sales side of the report only appears for orgs with ecommerce
 * enabled (the backend reports hasEcommerce and zeroes the rest).
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { MegaphoneIcon, ClipboardIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import TrackingConfigEditor from '@/components/cms/TrackingConfigEditor';
import { LoadingSpinner } from '@/components/common';
import { marketingApi, rolesApi, usersApi, type UtmLink, type CampaignReport, type AppAnnouncement } from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';

type TabId = 'pixeles' | 'utm' | 'campanas' | 'anuncios';

const TABS: { id: TabId; label: string }[] = [
  { id: 'pixeles', label: 'Píxeles' },
  { id: 'utm', label: 'UTM Builder' },
  { id: 'campanas', label: 'Campañas' },
  { id: 'anuncios', label: 'Anuncios' },
];

export default function Marketing() {
  const [activeTab, setActiveTab] = useState<TabId>('pixeles');
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MegaphoneIcon className="h-7 w-7 text-vida-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing</h1>
          <p className="text-sm text-gray-500">Píxeles de seguimiento, generador de UTM e informe de campañas.</p>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!selectedOrgId ? (
        <div className="p-8 text-center text-gray-500">Selecciona una organización.</div>
      ) : (
        <>
          {activeTab === 'pixeles' && <TrackingConfigEditor />}
          {activeTab === 'utm' && <UtmBuilderTab />}
          {activeTab === 'campanas' && <CampaignsTab />}
          {activeTab === 'anuncios' && <AnnouncementsTab />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UTM Builder
// ---------------------------------------------------------------------------

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function buildUrl(base: string, params: Record<string, string>): string {
  if (!base) return '';
  let url: URL;
  try {
    url = new URL(base);
  } catch {
    return '';
  }
  for (const [k, v] of Object.entries(params)) {
    if (v.trim()) url.searchParams.set(k, v.trim());
  }
  return url.toString();
}

function UtmBuilderTab() {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.MARKETING_MANAGE));

  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [term, setTerm] = useState('');

  const [links, setLinks] = useState<UtmLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fullUrl = useMemo(
    () =>
      buildUrl(baseUrl, {
        utm_source: source,
        utm_medium: medium,
        utm_campaign: campaign,
        utm_content: content,
        utm_term: term,
      }),
    [baseUrl, source, medium, campaign, content, term]
  );

  const loadLinks = async () => {
    setLoading(true);
    try {
      const res = await marketingApi.getUtmLinks();
      setLinks(res.data?.data || []);
    } catch {
      toast.error('Error al cargar el historial de campañas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLinks();
  }, []);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success('URL copiada'),
      () => toast.error('No se pudo copiar')
    );
  };

  const valid = name.trim() && fullUrl && source.trim() && medium.trim() && campaign.trim();

  const save = async () => {
    if (!valid) {
      toast.error('Completa nombre, URL base, source, medium y campaign');
      return;
    }
    setSaving(true);
    try {
      await marketingApi.createUtmLink({
        name: name.trim(),
        base_url: baseUrl.trim(),
        utm_source: source.trim(),
        utm_medium: medium.trim(),
        utm_campaign: campaign.trim(),
        utm_content: content.trim() || undefined,
        utm_term: term.trim() || undefined,
        full_url: fullUrl,
      });
      toast.success('Campaña guardada');
      setName('');
      setContent('');
      setTerm('');
      await loadLinks();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await marketingApi.deleteUtmLink(id);
      setLinks((prev) => prev.filter((l) => l._id !== id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Builder form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-900">Generador de URL</h2>
        <div>
          <label className={labelClass}>Nombre de la campaña *</label>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Rebajas verano newsletter" />
        </div>
        <div>
          <label className={labelClass}>URL base *</label>
          <input className={inputClass} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://tutienda.com/productos" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>utm_source *</label>
            <input className={inputClass} value={source} onChange={(e) => setSource(e.target.value)} placeholder="newsletter" />
          </div>
          <div>
            <label className={labelClass}>utm_medium *</label>
            <input className={inputClass} value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="email" />
          </div>
        </div>
        <div>
          <label className={labelClass}>utm_campaign *</label>
          <input className={inputClass} value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="rebajas_verano_2026" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>utm_content</label>
            <input className={inputClass} value={content} onChange={(e) => setContent(e.target.value)} placeholder="boton_cabecera" />
          </div>
          <div>
            <label className={labelClass}>utm_term</label>
            <input className={inputClass} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="zapatillas" />
          </div>
        </div>

        <div>
          <label className={labelClass}>URL generada</label>
          <div className="flex gap-2">
            <input readOnly className={`${inputClass} font-mono bg-gray-50 text-xs`} value={fullUrl} placeholder="Completa los campos…" />
            <button
              onClick={() => copy(fullUrl)}
              disabled={!fullUrl}
              className="shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <ClipboardIcon className="h-4 w-4" /> Copiar
            </button>
          </div>
        </div>

        {canManage && (
          <button
            onClick={save}
            disabled={saving || !valid}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50"
          >
            {saving && <LoadingSpinner size="sm" />} Guardar campaña
          </button>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Historial</h2>
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400">
            <LoadingSpinner size="lg" />
          </div>
        ) : links.length === 0 ? (
          <p className="text-sm text-gray-500">Aún no has guardado ninguna campaña.</p>
        ) : (
          <ul className="space-y-3 max-h-[28rem] overflow-y-auto">
            {links.map((l) => (
              <li key={l._id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{l.name}</p>
                    <p className="text-xs text-gray-500">
                      {l.utm_source} · {l.utm_medium} · {l.utm_campaign}
                    </p>
                    <p className="text-xs text-gray-400 font-mono truncate mt-1">{l.full_url}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copy(l.full_url)} className="p-1.5 text-gray-500 hover:text-gray-900" title="Copiar">
                      <ClipboardIcon className="h-4 w-4" />
                    </button>
                    {canManage && (
                      <button onClick={() => remove(l._id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Eliminar">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaigns report
// ---------------------------------------------------------------------------

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function CampaignsTab() {
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));

  const load = async () => {
    setLoading(true);
    try {
      const res = await marketingApi.getCampaignReport({ startDate, endDate });
      setReport(res.data?.data || null);
    } catch {
      toast.error('Error al cargar el informe de campañas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasEcommerce = report?.hasEcommerce ?? false;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-3 flex items-end gap-3 flex-wrap">
        <div>
          <label className={labelClass}>Desde</label>
          <input type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Hasta</label>
          <input type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <button onClick={() => void load()} className="px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
          Actualizar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <LoadingSpinner size="lg" />
        </div>
      ) : !report ? (
        <p className="text-sm text-gray-500">Sin datos.</p>
      ) : (
        <>
          {/* Totals */}
          <div className={`grid gap-4 ${hasEcommerce ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'}`}>
            <StatCard label="Visitas atribuidas" value={report.totals.visits.toLocaleString('es-ES')} />
            {hasEcommerce && <StatCard label="Pedidos" value={report.totals.orders.toLocaleString('es-ES')} />}
            {hasEcommerce && (
              <StatCard label="Ingresos atribuidos" value={`${report.totals.revenue.toFixed(2)} €`} />
            )}
          </div>

          {/* Campaigns table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Por campaña</h2>
            </div>
            {report.campaigns.length === 0 ? (
              <p className="px-6 py-8 text-sm text-gray-500 text-center">
                No hay tráfico con UTM en este periodo. Genera enlaces en la pestaña UTM Builder y úsalos en tus campañas.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Campaña</th>
                    <th className="text-left px-6 py-2 font-medium">Origen / Medio</th>
                    <th className="text-right px-6 py-2 font-medium">Visitas</th>
                    {hasEcommerce && <th className="text-right px-6 py-2 font-medium">Pedidos</th>}
                    {hasEcommerce && <th className="text-right px-6 py-2 font-medium">Ingresos</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.campaigns.map((c) => (
                    <tr key={c.campaign}>
                      <td className="px-6 py-3 font-medium text-gray-900">{c.campaign}</td>
                      <td className="px-6 py-3 text-gray-500">
                        {c.source || '—'}{c.medium ? ` / ${c.medium}` : ''}
                      </td>
                      <td className="px-6 py-3 text-right">{c.visits.toLocaleString('es-ES')}</td>
                      {hasEcommerce && <td className="px-6 py-3 text-right">{c.orders.toLocaleString('es-ES')}</td>}
                      {hasEcommerce && <td className="px-6 py-3 text-right">{c.revenue.toFixed(2)} €</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Channels */}
          {report.channels.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-3 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Por canal (origen / medio)</h2>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Origen</th>
                    <th className="text-left px-6 py-2 font-medium">Medio</th>
                    <th className="text-right px-6 py-2 font-medium">Visitas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.channels.map((ch, i) => (
                    <tr key={`${ch.source}-${ch.medium}-${i}`}>
                      <td className="px-6 py-3 text-gray-900">{ch.source}</td>
                      <td className="px-6 py-3 text-gray-500">{ch.medium}</td>
                      <td className="px-6 py-3 text-right">{ch.visits.toLocaleString('es-ES')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Announcements (web-publish) — public messages shown on the org's website.
// ---------------------------------------------------------------------------

type Audience = 'org' | 'roles' | 'users';
interface RoleLite { _id: string; name: string }
interface UserLite { _id: string; email: string; profile?: { first_name?: string; last_name?: string } }

function AnnouncementsTab() {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.MARKETING_MANAGE));
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [items, setItems] = useState<AppAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<'info' | 'warning' | 'critical'>('info');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  // Audience targeting (Module C). 'org' = public on the web + in-app to all;
  // 'roles'/'users' = internal, in-app only to the targets.
  const [audience, setAudience] = useState<Audience>('org');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [byEmail, setByEmail] = useState(false);
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [audienceLoaded, setAudienceLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await marketingApi.getAnnouncements();
      setItems(r.data?.data || []);
    } catch {
      toast.error('Error al cargar anuncios');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  // Lazily load roles + org members the first time the form is opened.
  useEffect(() => {
    if (!showForm || audienceLoaded) return;
    setAudienceLoaded(true);
    void (async () => {
      try {
        const [rr, ur] = await Promise.all([
          rolesApi.list(selectedOrgId),
          usersApi.list(1, 100),
        ]);
        setRoles((rr.data?.roles || rr.data?.data || rr.data || []) as RoleLite[]);
        setUsers((ur.data?.users || []) as UserLite[]);
      } catch {
        toast.error('Error al cargar roles/usuarios');
      }
    })();
  }, [showForm, audienceLoaded, selectedOrgId]);

  const userLabel = (u: UserLite) => {
    const name = [u.profile?.first_name, u.profile?.last_name].filter(Boolean).join(' ').trim();
    return name ? `${name} (${u.email})` : u.email;
  };

  const create = async () => {
    if (!title.trim()) { toast.error('El título es obligatorio'); return; }
    if (audience === 'roles' && roleIds.length === 0) { toast.error('Selecciona al menos un rol'); return; }
    if (audience === 'users' && userIds.length === 0) { toast.error('Selecciona al menos un usuario'); return; }
    setSaving(true);
    try {
      await marketingApi.createAnnouncement({
        title: title.trim(),
        body: body.trim() || undefined,
        level,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        audience,
        roleIds: audience === 'roles' ? roleIds : undefined,
        userIds: audience === 'users' ? userIds : undefined,
        email: audience !== 'org' ? byEmail : undefined,
      });
      toast.success(audience === 'org' ? 'Anuncio publicado' : 'Notificación enviada');
      setTitle(''); setBody(''); setLevel('info'); setExpiresAt('');
      setAudience('org'); setRoleIds([]); setUserIds([]); setByEmail(false); setShowForm(false);
      await load();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al publicar');
    } finally {
      setSaving(false);
    }
  };

  const toggleId = (list: string[], id: string) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const remove = async (id: string) => {
    try {
      await marketingApi.deleteAnnouncement(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Anuncios y avisos</h2>
          <p className="text-sm text-gray-500">Dirígelos a toda la organización (públicos en la web del org, requiere activar "Publicación en web") o a roles/usuarios concretos (solo in-app, privados).</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
            <PlusIcon className="h-4 w-4" /> Nuevo
          </button>
        )}
      </div>

      {showForm && canManage && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título" />
          <textarea className={inputClass} rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Texto (opcional)" />

          {/* Audience selector */}
          <div>
            <label className={labelClass}>Destinatarios</label>
            <div className="flex flex-wrap gap-2">
              {([
                { id: 'org', label: 'Toda la organización', hint: 'Público en la web + in-app' },
                { id: 'roles', label: 'Roles concretos', hint: 'Solo in-app' },
                { id: 'users', label: 'Usuarios concretos', hint: 'Solo in-app' },
              ] as { id: Audience; label: string; hint: string }[]).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAudience(opt.id)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${audience === opt.id ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {audience === 'roles' && (
            <div className="border border-gray-200 rounded-lg p-3 max-h-44 overflow-auto space-y-1">
              {roles.length === 0 ? <p className="text-sm text-gray-400">No hay roles disponibles.</p> : roles.map((r) => (
                <label key={r._id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={roleIds.includes(r._id)} onChange={() => setRoleIds((l) => toggleId(l, r._id))} />
                  {r.name}
                </label>
              ))}
            </div>
          )}
          {audience === 'users' && (
            <div className="border border-gray-200 rounded-lg p-3 max-h-44 overflow-auto space-y-1">
              {users.length === 0 ? <p className="text-sm text-gray-400">No hay usuarios disponibles.</p> : users.map((u) => (
                <label key={u._id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={userIds.includes(u._id)} onChange={() => setUserIds((l) => toggleId(l, u._id))} />
                  {userLabel(u)}
                </label>
              ))}
            </div>
          )}

          {audience !== 'org' && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={byEmail} onChange={(e) => setByEmail(e.target.checked)} />
              <span>
                Enviar también por email
                <span className="block text-xs text-gray-400">Respeta la preferencia de cada destinatario (quien desactivó el email o subió su nivel no lo recibe). Verás el estado en la campana.</span>
              </span>
            </label>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={labelClass}>Nivel</label>
              <select className={inputClass} value={level} onChange={(e) => setLevel(e.target.value as 'info' | 'warning' | 'critical')}>
                <option value="info">Info</option>
                <option value="warning">Aviso</option>
                <option value="critical">Importante</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Caduca (opcional)</label>
              <input type="date" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
            <button onClick={create} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
              {saving && <LoadingSpinner size="sm" />} {audience === 'org' ? 'Publicar' : 'Enviar'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No hay anuncios publicados.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {items.map((a) => (
            <div key={a._id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{a.title}</p>
                {a.body && <p className="text-xs text-gray-500 mt-0.5">{a.body}</p>}
                <p className="text-[11px] text-gray-400 mt-1">
                  {a.publicWeb ? 'Web pública + in-app' : (a.roleIds && a.roleIds.length > 0) ? `In-app · ${a.roleIds.length} rol(es)` : (a.userIds && a.userIds.length > 0) ? `In-app · ${a.userIds.length} usuario(s)` : 'In-app'}
                  {a.channels?.includes('email') ? ' + email' : ''}
                  {' · '}{a.level}{a.expiresAt ? ` · caduca ${new Date(a.expiresAt).toLocaleDateString('es-ES')}` : ''}
                </p>
              </div>
              {canManage && (
                <button onClick={() => remove(a._id)} className="p-1.5 text-gray-400 hover:text-red-600 shrink-0"><TrashIcon className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
